import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Clock, AlertCircle, BookOpen, ArrowLeft, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DownloadBook() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid download link");
      setLoading(false);
      return;
    }

    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/books/download/info?token=${token}`);
        setDownloadInfo(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired download link");
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    setError(""); // Clear any previous error
    try {
      // Use direct download URL - opens in browser for reliable download
      const downloadUrl = `${API}/books/direct-download?token=${token}`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${downloadInfo?.book_title || 'document'}.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Wait a moment then refresh info to update download count
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/books/download/info?token=${token}`);
          setDownloadInfo(res.data);
        } catch (e) {
          // Ignore refresh errors
        }
        setDownloading(false);
      }, 2000);
    } catch (err) {
      console.error("Download error:", err);
      setError("Download failed. Please try again.");
      setDownloading(false);
    }
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!downloadInfo?.expires_at) return null;
    const expires = new Date(downloadInfo.expires_at);
    const now = new Date();
    const diff = expires - now;
    if (diff <= 0) return "Expired";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-[#111] border border-zinc-800 p-8"
      >
        {error ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Download Unavailable</h2>
            <p className="text-zinc-400 font-dm-sans mb-6">{error}</p>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </div>
        ) : downloadInfo?.is_expired ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={32} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Link Expired</h2>
            <p className="text-zinc-400 font-dm-sans mb-6">
              This download link has expired. Please contact support for assistance.
            </p>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        ) : downloadInfo?.downloads_left === 0 ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-outfit font-bold text-white mb-2">Download Limit Reached</h2>
            <p className="text-zinc-400 font-dm-sans mb-6">
              You've reached the maximum number of downloads (5). Please contact support if you need additional access.
            </p>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen size={32} className="text-cyan-500" />
              </div>
              <h2 className="text-xl font-outfit font-bold text-white mb-1">Your Book is Ready</h2>
              <p className="text-zinc-400 font-dm-sans">Hi {downloadInfo?.buyer_name}!</p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-4 mb-6">
              <h3 className="font-outfit font-bold text-white text-lg mb-1">{downloadInfo?.book_title}</h3>
              <p className="text-sm text-zinc-400">by {downloadInfo?.book_author}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-900 border border-zinc-800 p-3 text-center">
                <Download size={18} className="mx-auto text-cyan-400 mb-1" />
                <p className="text-lg font-bold text-white">{downloadInfo?.downloads_left}</p>
                <p className="text-xs text-zinc-500">Downloads Left</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-3 text-center">
                <Clock size={18} className="mx-auto text-purple-400 mb-1" />
                <p className="text-sm font-bold text-white">{getTimeRemaining()}</p>
                <p className="text-xs text-zinc-500">Time Remaining</p>
              </div>
            </div>

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50 mb-3"
            >
              {downloading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download PDF
                </>
              )}
            </button>

            {/* Read Online Option */}
            <Link
              to={`/read-book?token=${token}`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:opacity-90 transition-opacity mb-4"
            >
              <BookOpen size={18} />
              Read Online Instead
            </Link>

            <p className="text-xs text-zinc-500 text-center mb-4">
              By downloading, you agree to our terms of use. This book is for personal use only.
            </p>

            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
